import { addDays } from "date-fns";
import { pick } from "lodash";

import {
  PRICE_SCENARIOS,
  isOneTimePrice,
} from "@/react/helpers/paywallPriceHelpers";
import type { PaywallPrice } from "@/react/types/PaywallPrice";
import { paywallPriceTypes } from "@/react/types/PaywallPrice";

import type { GetPreviewParametersReturn } from "../../helpers";
import {
  INTERVAL_TYPE_TO_PERIOD,
  calculateAmountWithDiscount,
  getDiscountAmount,
  getRecurrentPrice,
} from "../../helpers/mockedPreviewers";
import type { CheckoutProps, PaywallCheckout } from "../../types/CheckoutProps";

export interface GeneratorParameters<T> {
  checkoutPreviewContext: CheckoutProps["checkoutPreviewContext"];
  params: T;
  paywall: PaywallCheckout;
  selectedPrice?: PaywallPrice;
}

const getFirstAmount = (
  props: GeneratorParameters<GetPreviewParametersReturn>,
): number => {
  if (
    [
      PRICE_SCENARIOS.trialThenValue,
      PRICE_SCENARIOS.trialThenUpfrontThenValue,
    ].includes(props.selectedPrice?.scenario || "value")
  ) {
    return 0;
  }

  if (
    [
      PRICE_SCENARIOS.upfrontThenValue,
      PRICE_SCENARIOS.upfrontThenTrialThenValue,
    ].includes(props.selectedPrice?.scenario || "value")
  ) {
    return props.selectedPrice?.upfront_payment_amount || 0;
  }

  return getRecurrentPrice(props?.selectedPrice);
};

const getNumber = (input: undefined | string | number): number =>
  Number(input) || 0;

const getTrialDays = (
  props: GeneratorParameters<GetPreviewParametersReturn>,
): number => getNumber(props.selectedPrice?.trial_days);

const isTrialPeriodFree = (
  props: GeneratorParameters<GetPreviewParametersReturn>,
) =>
  [
    PRICE_SCENARIOS.trialThenValue,
    PRICE_SCENARIOS.trialThenUpfrontThenValue,
  ].includes(props?.selectedPrice?.scenario || "value");

const isTrialPeriodPaid = (
  props: GeneratorParameters<GetPreviewParametersReturn>,
) =>
  [PRICE_SCENARIOS.upfrontThenTrialThenValue].includes(
    props?.selectedPrice?.scenario || "value",
  );

const generateTrialPeriod = (
  props: GeneratorParameters<GetPreviewParametersReturn>,
) => {
  if (getTrialDays(props) <= 0) {
    return null;
  }

  return {
    days: props.selectedPrice?.trial_days,
    free: isTrialPeriodFree(props),
    paid: isTrialPeriodPaid(props),
    // The following locale string "en-CA" returns the format yyyy-mm-dd
    ends_on: addDays(
      new Date(),
      props.selectedPrice?.trial_days || 0,
    ).toLocaleDateString("en-CA"),
    // "formatted_ends_on": "" // We shouldn't be using formatted strings from Backend
  };
};

const generateFuturePayments = (
  props: GeneratorParameters<GetPreviewParametersReturn>,
) => {
  const [addPeriod, iterations] =
    INTERVAL_TYPE_TO_PERIOD[
      props.selectedPrice?.interval_type || paywallPriceTypes.onetime
    ];
  const firstPaymentDueOn = addDays(
    new Date(),
    isTrialPeriodFree(props) || isTrialPeriodPaid(props)
      ? getTrialDays(props)
      : 0,
  );

  const isOneInstallment =
    props.selectedPrice?.scenario === PRICE_SCENARIOS.value &&
    props.selectedPrice.installments_count === 1;
  const isOneTime = isOneTimePrice(props.selectedPrice);

  if (isOneInstallment || isOneTime) {
    return null;
  }

  return {
    // Probably we won't need to emulate the future payments mostly because
    // it is not possible to preview price modal when previewing the Checkout
    subtotal: 33333,
    discount: 0,
    tax: 0,
    total: 33333,
    starting_balance: 0,
    amount_due: 33333,
    carry_over_amount: 0,
    ending_balance: 0,
    start_on: addPeriod(firstPaymentDueOn, iterations).toLocaleDateString(
      "en-CA",
    ),
    // We shouldn't be using formatted strings from Backend
    // "discount_phrase": null,
    // "formatted_subtotal": "$333.33 USD",
    // "formatted_discount": "$0 USD",
    // "formatted_tax": "$0 USD",
    // "formatted_total": "$333.33 USD",
    // "formatted_starting_balance": "$0 USD",
    // "formatted_amount_due": "$333.33 USD",
    // "formatted_carry_over_amount": "$0 USD",
    // "formatted_ending_balance": "$0 USD",
    // "formatted_start_on": "Aug 6, 2025",
    // "start_on_phrase": "Quarterly payments starting on Aug 6, 2025",
    // "payment_schedule_line": "Quarterly on the 6th, cancel anytime."
  };
};

const generateFirstPayment = (
  props: GeneratorParameters<GetPreviewParametersReturn>,
) => {
  // The first payment is always after the trial period
  const dueOn = addDays(
    new Date(),
    isTrialPeriodFree(props) ? getTrialDays(props) : 0,
  );
  const amount = getFirstAmount(props);

  return {
    subtotal: props.selectedPrice?.amount,
    discount: getDiscountAmount(props, amount, props.selectedPrice),
    tax: 0,
    total: calculateAmountWithDiscount(props, amount, props.selectedPrice),
    starting_balance: 0,
    amount_due: calculateAmountWithDiscount(props, amount, props.selectedPrice),
    carry_over_amount: 0,
    ending_balance: 0,
    price_amount: amount,
    prorated_credit_description: "",
    prorated_credit_amount: 0,
    due_on: dueOn.toLocaleDateString("en-CA"),
    // We shouldn't be using formatted strings from Backend
    // "formatted_due_on": "May 6, 2025",
    // "price_description": "1 × Subscription (at $333.33 / every 3 months)",
    // "formatted_subtotal": "$333.33 USD",
    // "formatted_discount": "$0 USD",
    // "formatted_tax": "$0 USD",
    // "formatted_total": "$333.33 USD",
    // "formatted_starting_balance": "$0 USD",
    // "formatted_amount_due": "$333.33 USD",
    // "formatted_carry_over_amount": "$0 USD",
    // "formatted_ending_balance": "$0 USD",
    // "formatted_price_amount": "$333.33 USD",
    // "formatted_prorated_credit_amount": "$0 USD",
    // "due_on_phrase": "Today's payment"
  };
};

const generate = (props: GeneratorParameters<GetPreviewParametersReturn>) => {
  const firstPayment = generateFirstPayment(props);
  const futurePayments = generateFuturePayments(props);
  const remainingPayments = null;

  return {
    amount_due_now: firstPayment.amount_due,
    preview_scenario: props?.selectedPrice?.scenario || "value",
    community_id: props?.checkoutPreviewContext?.community_id,
    paywall_id: props.paywall.id,
    paywall_price_id: props?.selectedPrice?.id,
    free_forever: false, // TODO: Check if we need to emulate this value
    paywall_coupon_id: props?.checkoutPreviewContext?.coupon_applied?.id,
    paywall_coupon_code: props?.checkoutPreviewContext?.coupon_applied?.code,
    currency: props.paywall?.currency,
    trial_period: generateTrialPeriod(props),
    first_payment: firstPayment,
    future_payments: futurePayments,
    remaining_payments: remainingPayments,
    paywall_coupon: props?.checkoutPreviewContext?.coupon_applied,
    paywall_price: pick(props.selectedPrice, [
      "id",
      "interval_type",
      "price_type",
    ]),
    // We shouldn't be using formatted strings from Backend
    // "formatted_amount_due_now": "$100 USD",
  };
};

export const fetchMockedData =
  (
    paywall: PaywallCheckout,
    checkoutPreviewContext: CheckoutProps["checkoutPreviewContext"],
  ) =>
  async (params: GetPreviewParametersReturn) => {
    if (!params || !paywall) {
      return null;
    }

    const selectedPrice = paywall.prices.find(
      (price: PaywallPrice) => price.id === params.queryParams.paywall_price_id,
    );

    await new Promise(resolve => setTimeout(resolve, 500));

    return generate({ params, paywall, checkoutPreviewContext, selectedPrice });
  };
